import { useEffect, useState } from "react";

import { Trash2, Plus } from "lucide-react";

import { Combobox, type ComboOption } from "@/components/cinch/Combobox";
import { NumberInput } from "@/components/cinch/NumberInput";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

import { useItems, usePackageWrites, usePriceMetrics, type PackageRow } from "../api";
import { Field, writeError } from "./editorCommon";

interface PkgItemRow {
  _id: string;
  item: string | null;
  quantity_limit: number | null;
  included_price: number | null;
  display_as_unlimited: boolean;
}

let _seq = 0;
const newRow = (): PkgItemRow => ({
  _id: `r${_seq++}`,
  item: null,
  quantity_limit: null,
  included_price: null,
  display_as_unlimited: false,
});

/** Create/edit a billing package. `estimated_gross_margin` is entered as a percent
 *  but Cinch stores a fraction (0–1), so it's ÷100 on save and ×100 on load. */
export function PackageEditor({
  open,
  pkg,
  onClose,
}: {
  open: boolean;
  pkg: PackageRow | null;
  onClose: () => void;
}) {
  const { save } = usePackageWrites();
  const metricsQ = usePriceMetrics({ limit: 500, offset: 0 });
  const itemsQ = useItems({ limit: 500, offset: 0 });
  const metricOptions: ComboOption[] = (metricsQ.data?.results ?? []).map((m) => ({
    id: m.id,
    label: m.name || m.id,
  }));
  const itemOptions: ComboOption[] = (itemsQ.data?.results ?? []).map((i) => ({
    id: i.id,
    label: i.name || i.id,
  }));

  const [name, setName] = useState("");
  const [sku, setSku] = useState("");
  const [pricingType, setPricingType] = useState("per_unit");
  const [price, setPrice] = useState<number | null>(null);
  const [marginPctVal, setMarginPctVal] = useState<number | null>(null);
  const [proposalEligible, setProposalEligible] = useState(false);
  const [priceMetric, setPriceMetric] = useState<string | null>(null);
  const [descr, setDescr] = useState("");
  const [custDescr, setCustDescr] = useState("");
  const [rows, setRows] = useState<PkgItemRow[]>([]);

  useEffect(() => {
    if (!open) return;
    setName(pkg?.name ?? "");
    setSku(pkg?.sku ?? "");
    setPricingType(pkg?.pricing_type ?? "per_unit");
    setPrice(pkg?.price ?? null);
    setMarginPctVal(pkg?.estimated_gross_margin != null ? pkg.estimated_gross_margin * 100 : null);
    setProposalEligible(!!pkg?.proposal_eligible);
    setPriceMetric(pkg?.price_metric ?? null);
    setDescr(pkg?.descr ?? "");
    setCustDescr(pkg?.customer_facing_descr ?? "");
    setRows(
      (pkg?.package_items ?? []).map((pi) => ({
        _id: `r${_seq++}`,
        item: pi.item ?? null,
        quantity_limit: pi.quantity_limit ?? null,
        included_price: pi.included_price ?? null,
        display_as_unlimited: !!pi.display_as_unlimited,
      })),
    );
    save.reset();
  }, [open, pkg]); // eslint-disable-line react-hooks/exhaustive-deps

  const patchRow = (id: string, patch: Partial<PkgItemRow>) =>
    setRows((rs) => rs.map((r) => (r._id === id ? { ...r, ...patch } : r)));

  const submit = () => {
    const body = {
      name: name.trim() || null,
      sku: sku.trim() || null,
      pricing_type: pricingType,
      price,
      estimated_gross_margin: marginPctVal != null ? marginPctVal / 100 : null,
      proposal_eligible: proposalEligible,
      price_metric: priceMetric,
      descr: descr.trim() || null,
      customer_facing_descr: custDescr.trim() || null,
      package_items: rows
        .filter((r) => r.item)
        .map((r) => ({
          item: r.item,
          quantity_limit: r.display_as_unlimited ? null : r.quantity_limit,
          included_price: r.included_price,
          display_as_unlimited: r.display_as_unlimited,
        })),
    };
    save.mutate({ id: pkg?.id ?? null, body }, { onSuccess: onClose });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{pkg ? "Edit package" : "New package"}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Name" className="col-span-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Package name" />
          </Field>
          <Field label="SKU">
            <Input value={sku} onChange={(e) => setSku(e.target.value)} />
          </Field>
          <Field label="Pricing Type">
            <Select value={pricingType} onValueChange={setPricingType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="per_unit">Per Unit</SelectItem>
                <SelectItem value="flat_fee">Flat Fee</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Price">
            <NumberInput value={price} onChange={setPrice} prefix="$" />
          </Field>
          <Field label="Est. Gross Margin (%)">
            <NumberInput value={marginPctVal} onChange={setMarginPctVal} />
          </Field>
          <Field label="Price Metric">
            <Combobox
              options={metricOptions}
              value={priceMetric}
              onChange={setPriceMetric}
              placeholder="Select a price metric…"
              clearable
            />
          </Field>
          <Field label="Proposal Eligible">
            <div className="flex h-9 items-center">
              <Switch checked={proposalEligible} onCheckedChange={setProposalEligible} />
            </div>
          </Field>
          <Field label="Internal Description" className="col-span-2">
            <Textarea value={descr} onChange={(e) => setDescr(e.target.value)} rows={2} />
          </Field>
          <Field label="Customer-facing Description" className="col-span-2">
            <Textarea value={custDescr} onChange={(e) => setCustDescr(e.target.value)} rows={2} />
          </Field>
        </div>

        {/* Included items sub-builder */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Included Items
            </span>
            <Button variant="outline" size="sm" onClick={() => setRows((rs) => [...rs, newRow()])}>
              <Plus className="h-3.5 w-3.5" />
              Add item
            </Button>
          </div>
          {rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No included items.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {rows.map((r) => (
                <div key={r._id} className="grid grid-cols-[1fr_auto_auto_auto_auto] items-center gap-2">
                  <Combobox
                    options={itemOptions}
                    value={r.item}
                    onChange={(v) => patchRow(r._id, { item: v })}
                    placeholder="Item…"
                  />
                  <div className="w-28">
                    <NumberInput
                      value={r.quantity_limit}
                      onChange={(v) => patchRow(r._id, { quantity_limit: v })}
                      disabled={r.display_as_unlimited}
                      placeholder="Qty limit"
                    />
                  </div>
                  <div className="w-28">
                    <NumberInput
                      value={r.included_price}
                      onChange={(v) => patchRow(r._id, { included_price: v })}
                      prefix="$"
                      placeholder="Incl. price"
                    />
                  </div>
                  <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Switch
                      checked={r.display_as_unlimited}
                      onCheckedChange={(v) => patchRow(r._id, { display_as_unlimited: v })}
                    />
                    Unlimited
                  </label>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Remove"
                    onClick={() => setRows((rs) => rs.filter((x) => x._id !== r._id))}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {save.error && (
          <Alert variant="danger">
            <AlertDescription>{writeError(save.error)}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={save.isPending || !name.trim()}>
            {save.isPending ? "Saving…" : pkg ? "Save changes" : "Create package"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
